import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaRocket, FaShoppingCart, FaChartLine, FaComments, FaCheck, FaBolt, FaTwitter, FaLightbulb, FaDatabase, FaBullseye, FaTimes, FaExternalLinkAlt, FaGithub, FaKey, FaCode, FaFolder, FaHome, FaTools, FaUser, FaEnvelope, FaPaperPlane } from 'react-icons/fa';
import { 
  SiReact, 
  SiPython, 
  SiJavascript, 
  SiNodedotjs, 
  SiMongodb, 
  SiDocker, 
  SiExpress, 
  SiTypescript, 
  SiPostgresql, 
  SiSocketdotio, 
  SiRedis, 
  SiChartdotjs, 
  SiDjango, 
  SiVuedotjs, 
  SiGraphql,
  SiHtml5,
  SiCss3,
  SiGit,
  SiMysql,
  SiFigma,
  SiNextdotjs,
  SiTailwindcss,
  SiBootstrap,
  SiSass,
  SiJest,
  SiWebpack,
  SiFirebase,
  SiAmazonaws,
  SiAngular,
  SiPhp,
  SiOpenjdk,
  SiCsharp,
  SiGo,
  SiRust,
  SiSwift,
  SiKotlin,
  SiReact as SiReactNative 
} from 'react-icons/si';
import './App.css';

// Fonction pour générer la palette de couleurs (identique à App.js)
const generateColorPalette = (baseColor) => {
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const lighter = `rgb(${Math.min(255, r + 30)}, ${Math.min(255, g + 30)}, ${Math.min(255, b + 30)})`;
  const darker = `rgb(${Math.max(0, r - 30)}, ${Math.max(0, g - 30)}, ${Math.max(0, b - 30)})`;
  const gradient = `linear-gradient(135deg, ${baseColor} 0%, ${darker} 100%)`;
  
  return { lighter, darker, gradient, r, g, b };
};

// Fonction pour appliquer les variables CSS (identique à App.js)
const applyThemeAndColor = (isDark, accentColor) => {
  document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  document.documentElement.style.setProperty('--accent-color', accentColor);
  
  const palette = generateColorPalette(accentColor);
  
  document.documentElement.style.setProperty('--accent-gradient', palette.gradient);
  document.documentElement.style.setProperty('--accent-light', palette.lighter);
  document.documentElement.style.setProperty('--accent-dark', palette.darker);
  document.documentElement.style.setProperty('--accent-color-rgb', `${palette.r}, ${palette.g}, ${palette.b}`);
};

// Mapping des technologies avec leurs icônes et couleurs (doit correspondre à Admin.js)
const techConfig = {
  // Frontend
  'HTML': { icon: SiHtml5, color: '#E34F26', level: 90 },
  'CSS': { icon: SiCss3, color: '#1572B6', level: 90 },
  'HTML5': { icon: SiHtml5, color: '#E34F26', level: 90 },
  'CSS3': { icon: SiCss3, color: '#1572B6', level: 90 },
  'JavaScript': { icon: SiJavascript, color: '#F7DF1E', level: 92 },
  'TypeScript': { icon: SiTypescript, color: '#3178C6', level: 85 },
  'React': { icon: SiReact, color: '#61DAFB', level: 95 },
  'Vue.js': { icon: SiVuedotjs, color: '#4FC08D', level: 75 },
  'Angular': { icon: SiAngular, color: '#DD0031', level: 70 },
  'Next.js': { icon: SiNextdotjs, color: '#000000', level: 80 },
  'Tailwind CSS': { icon: SiTailwindcss, color: '#06B6D4', level: 85 },
  'Bootstrap': { icon: SiBootstrap, color: '#7952B3', level: 80 },
  'Sass': { icon: SiSass, color: '#CC6699', level: 75 },
  
  // Backend
  'Node.js': { icon: SiNodedotjs, color: '#339933', level: 88 },
  'Express': { icon: SiExpress, color: '#404040', level: 82 },
  'Python': { icon: SiPython, color: '#3776AB', level: 90 },
  'Django': { icon: SiDjango, color: '#092E20', level: 75 },
  'PHP': { icon: SiPhp, color: '#777BB4', level: 75 },
  'Java': { icon: SiOpenjdk, color: '#ED8B00', level: 70 },
  'C#': { icon: SiCsharp, color: '#239120', level: 70 },
  'Go': { icon: SiGo, color: '#00ADD8', level: 65 },
  'Rust': { icon: SiRust, color: '#000000', level: 60 },
  
  // Bases de données
  'MongoDB': { icon: SiMongodb, color: '#47A248', level: 85 },
  'PostgreSQL': { icon: SiPostgresql, color: '#336791', level: 80 },
  'MySQL': { icon: SiMysql, color: '#4479A1', level: 85 },
  'Redis': { icon: SiRedis, color: '#DC382D', level: 75 },
  
  // Outils et DevOps
  'Docker': { icon: SiDocker, color: '#2496ED', level: 85 },
  'Git': { icon: SiGit, color: '#F05032', level: 85 },
  'Webpack': { icon: SiWebpack, color: '#8DD6F9', level: 70 },
  'Jest': { icon: SiJest, color: '#C21325', level: 75 },
  
  // Services et Cloud
  'Firebase': { icon: SiFirebase, color: '#FFCA28', level: 75 },
  'AWS': { icon: SiAmazonaws, color: '#FF9900', level: 70 },
  
  // Communication et autres
  'Socket.io': { icon: SiSocketdotio, color: '#010101', level: 78 },
  'GraphQL': { icon: SiGraphql, color: '#E10098', level: 70 },
  'Chart.js': { icon: SiChartdotjs, color: '#FF6384', level: 70 },
  'Figma': { icon: SiFigma, color: '#F24E1E', level: 80 },
  'JWT': { icon: FaKey, color: '#000000', level: 80 },
  
  // Mobile
  'Swift': { icon: SiSwift, color: '#FA7343', level: 60 },
  'Kotlin': { icon: SiKotlin, color: '#7F52FF', level: 60 },
  'React Native': { icon: SiReactNative, color: '#61DAFB', level: 85 }
};

// Mapping des icônes par nom
const iconMap = {
  'FaShoppingCart': FaShoppingCart,
  'FaChartLine': FaChartLine,
  'FaComments': FaComments,
  'FaCheck': FaCheck,
  'FaBolt': FaBolt,
  'FaTwitter': FaTwitter,
  'FaLightbulb': FaLightbulb,
  'FaDatabase': FaDatabase,
  'FaBullseye': FaBullseye,
  'FaRocket': FaRocket,
  'FaCode': FaCode,
  'FaFolder': FaFolder,
  'FaHome': FaHome,
  'FaTools': FaTools,
  'FaUser': FaUser,
  'FaEnvelope': FaEnvelope
};

// (Projets par défaut retirés; la liste provient du localStorage)

function AllProjects() {
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [allProjects, setAllProjects] = useState([]);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');

  // Charger et appliquer le thème et la couleur depuis localStorage SYNCHRONEMENT
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const savedColor = localStorage.getItem('accentColor');
    const isDark = savedTheme ? savedTheme === 'dark' : true;
    const accentColor = savedColor || '#3b82f6';
    
    // Appliquer immédiatement les variables CSS
    applyThemeAndColor(isDark, accentColor);
    
    // Charger le message de loading personnalisé
    const customMessage = localStorage.getItem('loadingMessage');
    if (customMessage) {
      setLoadingMessage(customMessage);
      localStorage.removeItem('loadingMessage'); // Nettoyer après lecture
    } else {
      setLoadingMessage('Portfolio Développeur Full Stack');
    }
  }, []);

  // Animation du loading screen
  useEffect(() => {
    const progressInterval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => {
            setIsLoading(false);
            // Démarrer l'animation d'entrée après la fin du loading
            setTimeout(() => {
              setIsPageLoaded(true);
            }, 100);
          }, 300);
          return 100;
        }
        return prev + 2;
      });
    }, 40); // Augmente de 2% toutes les 40ms = 2 secondes pour atteindre 100%

    return () => clearInterval(progressInterval);
  }, []);

  // Fonction pour déterminer l'origine de l'API (priorité à REACT_APP_API_ORIGIN)
  const getApiUrl = (endpoint) => {
    const baseUrl = (process.env.REACT_APP_API_ORIGIN || 'https://mrtchoukdev.com').replace(/\/$/, '');
    const apiUrl = `${baseUrl}/api${endpoint}`;
    console.log('🔗 API URL:', { baseUrl, apiUrl });
    return apiUrl;
  };

  // Charger les projets depuis l'API
  useEffect(() => {
    if (!isLoading) {
      const loadProjects = async () => {
        try {
          const apiUrl = `${getApiUrl('/projects.php')}?t=${Date.now()}`;
          console.log('📦 Chargement des projets depuis:', apiUrl);
          
          let response;
          try {
            response = await fetch(apiUrl, {
              method: 'GET',
              mode: 'cors',
              credentials: 'omit',
              headers: {
                'Accept': 'application/json',
                'Cache-Control': 'no-cache'
              },
              cache: 'no-store'
            });
            console.log('📦 Response status:', response.status, response.statusText);
          } catch (fetchError) {
            console.error('📦 Fetch error (network):', fetchError);
            // Erreur réseau (pas de réponse du serveur)
            if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('NetworkError')) {
              throw new Error(`Impossible de se connecter au serveur. Vérifiez que:\n- Le serveur est accessible\n- Le certificat SSL est accepté\n- Le port 3333 n'est pas bloqué`);
            }
            throw fetchError;
          }
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('📦 API Error Response:', errorText);
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          const text = await response.text();
          let data = {};
          try {
            data = text ? JSON.parse(text) : {};
          } catch (e) {
            console.error('📦 JSON parse error:', e);
            throw new Error('Réponse serveur invalide (JSON attendu)');
          }
          console.log('📦 Réponse API:', { success: data.success, count: data.projects?.length || 0, data });
          
          if (data.success && Array.isArray(data.projects)) {
            // Convertir les icônes string en composants et les chemins d'images
            const isLocalhost = window.location.hostname === 'localhost' || 
                                window.location.hostname === '127.0.0.1' ||
                                window.location.hostname === '';
            const isDevOnVPS = !isLocalhost && window.location.port === '3334';
            const baseUrl = isLocalhost || isDevOnVPS
              ? 'https://51.91.110.70:3333'
              : window.location.origin;
            
            const assetBase = (process.env.REACT_APP_API_ORIGIN || 'https://mrtchoukdev.com').replace(/\/$/, '');

            const projectsWithIcons = data.projects.map(project => {
              let imageUrl = project.image || null;
              let cardImageUrl = project.cardImage || null;

              if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('data:')) {
                if (!imageUrl.startsWith('/')) imageUrl = '/' + imageUrl;
                imageUrl = assetBase + imageUrl;
              }
              if (cardImageUrl && !cardImageUrl.startsWith('http') && !cardImageUrl.startsWith('data:')) {
                if (!cardImageUrl.startsWith('/')) cardImageUrl = '/' + cardImageUrl;
                cardImageUrl = assetBase + cardImageUrl;
              }

              return {
                ...project,
                id: project.id,
                image: imageUrl,
                cardImage: cardImageUrl,
                icon: iconMap[project.icon] || FaShoppingCart
              };
            });
            
            setAllProjects(projectsWithIcons);
          } else {
            console.error('📦 API retourne un format invalide:', data);
            // Ne pas utiliser localStorage, afficher un message d'erreur
            setAllProjects([]);
            alert('Erreur: Impossible de charger les projets depuis le serveur. Vérifiez votre connexion.');
          }
        } catch (error) {
          console.error('📦 Erreur lors du chargement des projets:', error);
          console.error('📦 Error details:', {
            message: error.message,
            stack: error.stack,
            name: error.name,
            hostname: window.location.hostname,
            port: window.location.port,
            protocol: window.location.protocol
          });
          
          // Ne pas utiliser localStorage, afficher un message d'erreur
          setAllProjects([]);
          
          // Message d'erreur plus détaillé
          let errorMsg = error.message || 'Erreur inconnue';
          let helpMsg = '';
          
          if (errorMsg.includes('Failed to fetch') || errorMsg.includes('NetworkError') || errorMsg.includes('load fail')) {
            helpMsg = '\n\nCauses possibles:\n- Le serveur n\'est pas accessible depuis votre réseau\n- Le certificat SSL doit être accepté (certificat auto-signé)\n- Le port 3333 est peut-être bloqué par un firewall\n\nEssayez d\'ouvrir directement: https://51.91.110.70:3333/api/projects.php';
          } else if (errorMsg.includes('SSL') || errorMsg.includes('certificate')) {
            helpMsg = '\n\nLe certificat SSL doit être accepté. Acceptez le certificat dans votre navigateur.';
          }
          
          alert(`Erreur: Impossible de charger les projets depuis le serveur.\n\nDétails: ${errorMsg}${helpMsg}`);
        }
      };
      
      loadProjects();
    }
  }, [isLoading]);

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setIsModalClosing(false);
    document.body.style.overflow = 'hidden'; // Empêcher le scroll
  };

  const closeModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setSelectedProject(null);
      setIsModalClosing(false);
      document.body.style.overflow = 'unset'; // Réactiver le scroll
    }, 300);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-background">
          <div className="loading-orb orb-1"></div>
          <div className="loading-orb orb-2"></div>
          <div className="loading-orb orb-3"></div>
        </div>
        <div className="loading-content">
          <div className="loading-logo-container">
            <div className="loading-logo-ring"></div>
            <div className="loading-logo">
              <FaFolder />
            </div>
          </div>
          <div className="loading-text">
            <h1 className="loading-title">
              <span className="loading-name">Mrtchouk</span>
              <span className="loading-year">{new Date().getFullYear()}</span>
            </h1>
            <p className="loading-subtitle">{loadingMessage || 'Portfolio Développeur Full Stack'}</p>
          </div>
          <div className="loading-bar-container">
            <div className="loading-bar">
              <div className="loading-progress" style={{ width: `${loadingProgress}%` }}></div>
            </div>
            <div className="loading-percentage">{loadingProgress}%</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
    <section className={`all-projects ${isPageLoaded ? 'page-loaded' : ''}`} style={{ 
      padding: '5rem 2rem',
      minHeight: '100vh',
      background: 'var(--bg-primary)'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header avec bouton retour */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '3rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/')}
            style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              padding: '0.75rem 1.5rem'
            }}
          >
            <FaArrowLeft />
            <span>Retour</span>
          </button>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: '800',
            background: 'var(--accent-gradient)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textAlign: 'center',
            flex: 1
          }}>
            Toutes mes réalisations
          </h2>
          <div style={{ width: '120px' }}></div>
        </div>

        {/* Grille complète de tous les projets */}
        <div className="projects-grid" style={{ marginTop: 0 }}>
          {allProjects.length > 0 ? allProjects.map((project) => {
            const IconComponent = project.icon;
            return (
              <div 
                key={project.id} 
                className={`project-card ${project.featured ? 'featured' : ''}`}
                onClick={() => handleProjectClick(project)}
                style={{ cursor: 'pointer' }}
              >
                <div className="project-image">
                  {project.cardImage ? (
                    <img src={project.cardImage} alt={project.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div className="project-placeholder"><IconComponent /></div>
                  )}
                  {project.featured && (
                    <div className="featured-badge">
                      <span><FaRocket /> Projet phare</span>
                    </div>
                  )}
                </div>
                <div className="project-content">
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-description">{project.description}</p>
                  <div className="project-technologies">
                    {project.technologies.map((tech, index) => (
                      <span key={index} className="tech-tag">{typeof tech === 'string' ? tech : tech.name}</span>
                    ))}
                  </div>
                </div>
              </div>
            );
          }) : (
            <div style={{ 
              gridColumn: '1 / -1', 
              textAlign: 'center', 
              padding: '3rem',
              color: 'var(--text-secondary)'
            }}>
              <p>Aucun projet disponible</p>
            </div>
          )}
        </div>
      </div>
    </section>

    {/* Modal de détails du projet */}
    {selectedProject && (
      <div 
        className={`project-modal-overlay ${isModalClosing ? 'closing' : ''}`}
        onClick={handleOverlayClick}
      >
        <div className={`project-modal ${isModalClosing ? 'closing' : ''}`}>
          <button 
            className="project-modal-close"
            onClick={closeModal}
            aria-label="Fermer"
          >
            <FaTimes />
          </button>

          {/* Image du projet */}
          <div className="project-modal-image">
            {selectedProject.image ? (
              <img src={selectedProject.image} alt={selectedProject.title} />
            ) : (
              <div className="project-modal-image-placeholder">
                {(() => {
                  const IconComponent = selectedProject.icon;
                  return <IconComponent style={{ fontSize: '4rem', color: 'var(--accent-color)' }} />;
                })()}
              </div>
            )}
            {selectedProject.featured && (
              <div className="featured-badge">
                <span><FaRocket /> Projet phare</span>
              </div>
            )}
          </div>

          {/* Contenu du modal */}
          <div className="project-modal-content">
            <h2 className="project-modal-title">{selectedProject.title}</h2>
            
            <p className="project-modal-description">
              {selectedProject.longDescription || selectedProject.description}
            </p>

            {/* Technologies avec pourcentages */}
            <div className="project-modal-technologies">
              <h3 className="project-modal-subtitle">Technologies utilisées</h3>
              <div className="project-tech-list">
                {selectedProject.technologies.map((tech, index) => {
                  const techInfo = techConfig[tech.name] || { icon: SiReact, color: '#61DAFB', level: tech.level || 80 };
                  const TechIcon = techInfo.icon;
                  const level = tech.level || techInfo.level || 80;
                  
                  return (
                    <div key={index} className="project-tech-item">
                      <div className="project-tech-header">
                        <div className="project-tech-info">
                          <TechIcon style={{ color: techInfo.color, fontSize: '1.5rem' }} />
                          <span className="project-tech-name">{tech.name}</span>
                        </div>
                        <span className="project-tech-percentage">{level}%</span>
                      </div>
                      <div className="project-tech-bar">
                        <div 
                          className="project-tech-fill"
                          style={{ 
                            width: `${level}%`,
                            backgroundColor: techInfo.color
                          }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Liens vers le projet */}
            <div className="project-modal-actions">
              {selectedProject.link && selectedProject.link !== '#' && (
                <a 
                  href={selectedProject.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="project-modal-link secondary"
                >
                  <FaExternalLinkAlt />
                  <span>Voir le projet</span>
                </a>
              )}
              <a 
                href="/#contact"
                onClick={(e) => {
                  e.preventDefault();
                  // Fermer la modale d'abord
                  setSelectedProject(null);
                  
                  // Naviguer vers la page d'accueil avec le hash #contact
                  // Le useEffect dans HomePage détectera le hash et scrollera automatiquement
                  navigate('/#contact');
                }}
                className="project-modal-link secondary"
              >
                <FaPaperPlane />
                <span>Me contacter</span>
              </a>
              {selectedProject.githubLink && (
                <a 
                  href={selectedProject.githubLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="project-modal-link secondary"
                >
                  <FaGithub />
                  <span>Code source</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    )}
    </div>
  );
}

export default AllProjects;

